// AI Service Module
// Handles all AI API interactions and provider-specific logic


const GEMINI_BASE_URL_FRAGMENT = 'generativelanguage.googleapis.com';
const OPENROUTER_BASE_URL_FRAGMENT = 'openrouter.ai';

function isGeminiBaseURL(baseURL) {
    return typeof baseURL === 'string' && baseURL.includes(GEMINI_BASE_URL_FRAGMENT);
}

function isOpenRouterBaseURL(baseURL) {
    return typeof baseURL === 'string' && baseURL.includes(OPENROUTER_BASE_URL_FRAGMENT);
}

function isGeminiModelName(model) {
    if (typeof model !== 'string') return false;
    return model.toLowerCase().includes('gemini');
}

function resolveDefaultModel(baseURL) {
    if (isGeminiBaseURL(baseURL)) {
        return 'gemini-2.5-flash';
    }

    if (isOpenRouterBaseURL(baseURL)) {
        return 'openai/gpt-4o-mini';
    }

    return 'gpt-3.5-turbo';
}

function getExtraHeaders(baseURL) {
    if (isGeminiBaseURL(baseURL)) {
        return {};
    }

    if (isOpenRouterBaseURL(baseURL)) {
        return {
            "HTTP-Referer": window.location.origin,
            "X-Title": "Infinite Canvas AI"
        };
    }

    return {};
}

function normalizeMessages(messages = [], systemPrompt = null) {
    if (!Array.isArray(messages) || messages.length === 0) {
        throw new Error('Conversation history is empty');
    }

    const sanitized = messages
        .map(message => ({
            role: message?.role || 'user',
            content: (message?.content || '').toString()
        }))
        .filter(message => message.content.trim().length > 0);

    if (sanitized.length === 0) {
        throw new Error('Conversation history is empty');
    }

    if (systemPrompt && systemPrompt.trim()) {
        const systemMessage = { role: 'system', content: systemPrompt.trim() };
        if (sanitized[0]?.role === 'system') {
            sanitized[0] = systemMessage;
        } else {
            sanitized.unshift(systemMessage);
        }
    }

    return sanitized;
}

function buildIdeaMessages(selectedNodeText, connectedNodes = []) {
    const messages = [];

    if (Array.isArray(connectedNodes) && connectedNodes.length > 0) {
        connectedNodes.forEach(node => {
            if (!node || !node.text) return;
            const trimmed = node.text.toString().trim();
            if (trimmed.length === 0) return;
            messages.push({ role: 'user', content: trimmed });
        });
        console.log('📎 Using', connectedNodes.length, 'connected nodes as conversation history');
    } else {
        console.log('📝 No connected nodes, starting new conversation');
    }

    if (typeof selectedNodeText === 'string' && selectedNodeText.trim().length > 0) {
        messages.push({ role: 'user', content: selectedNodeText.trim() });
    } else {
        throw new Error('Selected node text is required for generation');
    }

    return messages;
}

function normalizeModelDescriptors(models = []) {
    if (!Array.isArray(models)) {
        return [];
    }

    return models
        .map((entry, index) => {
            if (typeof entry === 'string') {
                const name = entry.trim();
                if (!name) {
                    return null;
                }
                return {
                    name,
                    key: `${name}__${index}`,
                    label: name
                };
            }

            if (entry && typeof entry === 'object') {
                const rawName = typeof entry.name === 'string'
                    ? entry.name
                    : (typeof entry.model === 'string' ? entry.model : '');
                const name = rawName.trim();
                if (!name) {
                    return null;
                }

                const rawKey = typeof entry.key === 'string' ? entry.key.trim() : '';
                const key = rawKey || `${name}__${index}`;
                const labelSource = typeof entry.label === 'string' ? entry.label.trim() : '';

                return {
                    name,
                    key,
                    label: labelSource || name,
                    providerHint: typeof entry.provider === 'string' ? entry.provider : null
                };
            }

            return null;
        })
        .filter(Boolean);
}

function validateCredentials(apiKey, baseURL) {
    if (!apiKey || apiKey.trim() === '') {
        throw new Error('API key is required');
    }

    if (!baseURL || baseURL.trim() === '') {
        throw new Error('Base URL is required');
    }

    if (baseURL.includes('openrouter.ai') && apiKey.startsWith('sk-proj-')) {
        throw new Error('You are using an OpenAI API key with OpenRouter. Please either:\n1. Change Base URL to: https://api.openai.com/v1\n2. Or get an OpenRouter API key from openrouter.ai');
    }

    if (baseURL.includes('api.openai.com') && apiKey.startsWith('sk-or-')) {
        throw new Error('You are using an OpenRouter API key with OpenAI. Please either:\n1. Change Base URL to: https://openrouter.ai/api/v1\n2. Or use your OpenAI API key instead');
    }
}

function normalizeProviderEntry(config = {}) {
    return {
        apiKey: typeof config.apiKey === 'string' ? config.apiKey.trim() : '',
        baseURL: typeof config.baseURL === 'string' ? config.baseURL.trim() : ''
    };
}

export function resolveModelCredentials(model, providers = {}) {
    if (!model || typeof model !== 'string') {
        throw new Error('Model name is required.');
    }

    const normalizedProviders = {
        primary: normalizeProviderEntry(providers.primary || {}),
        secondary: normalizeProviderEntry(providers.secondary || {})
    };

    const candidates = [];
    const requiresGemini = isGeminiModelName(model);

    const tryAddCandidate = (key, entry, options = {}) => {
        if (!entry.apiKey || !entry.baseURL) return;

        const baseIsGemini = isGeminiBaseURL(entry.baseURL);
        const baseSupportsGemini = baseIsGemini || isOpenRouterBaseURL(entry.baseURL);

        if (options.requireGeminiSupport && !baseSupportsGemini) return;
        if (options.excludeGeminiBase && baseIsGemini) return;

        candidates.push({ key, entry });
    };

    if (requiresGemini) {
        tryAddCandidate('secondary', normalizedProviders.secondary, { requireGeminiSupport: true });
        tryAddCandidate('primary', normalizedProviders.primary, { requireGeminiSupport: true });

        if (candidates.length === 0) {
            throw new Error('No Gemini-compatible provider configured. Add a Google Gemini base URL or use OpenRouter.');
        }
    } else {
        tryAddCandidate('primary', normalizedProviders.primary, { excludeGeminiBase: true });
        tryAddCandidate('secondary', normalizedProviders.secondary, { excludeGeminiBase: true });

        if (candidates.length === 0) {
            tryAddCandidate('primary', normalizedProviders.primary);
            tryAddCandidate('secondary', normalizedProviders.secondary);
        }

        if (candidates.length === 0) {
            throw new Error(`No compatible provider configured for model "${model}".`);
        }
    }

    const selected = candidates[0];
    if (!selected) {
        throw new Error(`No API credentials configured for model "${model}".`);
    }

    return {
        apiKey: selected.entry.apiKey,
        baseURL: selected.entry.baseURL,
        providerKey: selected.key
    };
}

export function getProviderName(baseURL) {
    if (!baseURL) return 'Demo Mode (Groq)';
    if (isOpenRouterBaseURL(baseURL)) return 'OpenRouter';
    if (baseURL.includes('api.openai.com')) return 'OpenAI';
    if (baseURL.includes('api.groq.com')) return 'Groq';
    if (isGeminiBaseURL(baseURL)) return 'Google Gemini';
    if (baseURL.includes('localhost') || baseURL.includes('127.0.0.1')) return 'Local API';
    return 'Custom API';
}

export async function generateAIIdeas(apiKey, baseURL, selectedNodeText, connectedNodes = [], model = null, systemPrompt = null) {
    const messages = buildIdeaMessages(selectedNodeText, connectedNodes);

    try {
        const responseText = await streamChatCompletion({
            apiKey,
            baseURL,
            messages,
            model,
            systemPrompt
        });
        const trimmedResponse = responseText.trim();

        if (!trimmedResponse) {
            throw new Error('AI generated empty response');
        }

        return [trimmedResponse]; // Always return as array with single item
    } catch (apiError) {
        console.error('❌ AI API Error:', apiError.message);

        // Re-throw the error to be handled by the calling function
        throw apiError;
    }
}

export async function sendChatCompletion(apiKey, baseURL, messages, model = null, systemPrompt = null) {
    return streamChatCompletion({
        apiKey,
        baseURL,
        messages,
        model,
        systemPrompt
    });
}

async function generateWithGemini(apiKey, baseURL, model, messages) {
    const trimmedBase = baseURL.trim();
    const sanitizedBase = trimmedBase.replace(/\/$/, '');

    let endpoint;
    if (sanitizedBase.endsWith(':generateContent')) {
        endpoint = sanitizedBase;
    } else {
        const modelBaseMatch = sanitizedBase.match(/(.*\/models)(?:\/[^:]+(?::generateContent)?)?$/);
        if (modelBaseMatch) {
            endpoint = `${modelBaseMatch[1]}/${model}:generateContent`;
        } else {
            endpoint = `${sanitizedBase}/models/${model}:generateContent`;
        }
    }

    const contents = messages
        .filter(message => message && typeof message.content === 'string' && message.content.trim().length > 0)
        .map((message) => ({
            role: message.role === 'assistant' ? 'model' : 'user',
            parts: [{ text: message.content }]
        }));

    if (contents.length === 0) {
        throw new Error('No valid content provided for Gemini request');
    }

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-goog-api-key': apiKey
        },
        body: JSON.stringify({
            contents,
            generationConfig: {
                temperature: 0.7
            }
        })
    });

    let responseData;
    try {
        responseData = await response.json();
    } catch (parseError) {
        if (!response.ok) {
            throw new Error(`Gemini request failed with status ${response.status}`);
        }
        throw new Error('Unexpected response from Gemini API');
    }

    if (!response.ok) {
        const errorMessage = responseData?.error?.message || responseData?.message || `Gemini request failed with status ${response.status}`;
        throw new Error(errorMessage);
    }

    const responseText = (responseData?.candidates || [])
        .flatMap(candidate => candidate?.content?.parts || [])
        .map(part => (typeof part.text === 'string' ? part.text.trim() : ''))
        .filter(Boolean)
        .join('\n')
        .trim();

    if (!responseText) {
        throw new Error('Gemini generated empty response');
    }

    console.log('✅ AI response:', responseText);
    return responseText;
}

export function getErrorMessage(error) {
    let errorMessage = 'Failed to generate AI ideas. ';

    // Check for specific error patterns
    const errorString = error.message || JSON.stringify(error) || '';

    if (errorString.includes('No auth credentials found') || errorString.includes('401')) {
        errorMessage += 'Authentication failed. Please check your API key and make sure it\'s valid for the selected provider.';
    } else if (errorString.includes('API key')) {
        errorMessage += 'Please check your API key.';
    } else if (errorString.includes('quota') || errorString.includes('insufficient_quota')) {
        errorMessage += 'API quota exceeded.';
    } else if (errorString.includes('network') || errorString.includes('fetch')) {
        errorMessage += 'Network error. Please try again.';
    } else if (errorString.includes('unauthorized')) {
        errorMessage += 'Invalid API key or unauthorized access.';
    } else if (errorString.includes('429') || errorString.includes('rate limit')) {
        errorMessage += 'Rate limit exceeded. Please wait and try again.';
    } else if (errorString.includes('model') && errorString.includes('not found')) {
        errorMessage += 'The requested model is not available. Please try a different model.';
    } else {
        errorMessage += errorString || 'Unknown error occurred.';
    }

    return errorMessage;
}

function buildChatEndpoint(baseURL) {
    const trimmed = (baseURL || '').replace(/\/$/, '');
    if (trimmed.endsWith('/chat/completions')) {
        return trimmed;
    }
    return `${trimmed}/chat/completions`;
}

function buildAuthHeaders(apiKey, baseURL, acceptHeader = 'application/json') {
    const headers = {
        'Content-Type': 'application/json',
        Accept: acceptHeader,
        Authorization: `Bearer ${apiKey}`
    };

    const extraHeaders = getExtraHeaders(baseURL);
    Object.entries(extraHeaders).forEach(([key, value]) => {
        headers[key] = value;
    });

    return headers;
}

export async function streamChatCompletion({ apiKey, baseURL, messages, model = null, systemPrompt = null, temperature = 0.7, onToken = null }) {
    validateCredentials(apiKey, baseURL);

    const finalMessages = normalizeMessages(messages, systemPrompt);

    const usingGemini = isGeminiBaseURL(baseURL);
    const finalModel = model && model.trim().length > 0 ? model.trim() : resolveDefaultModel(baseURL);
    const providerName = getProviderName(baseURL);

    console.log('🤖 AI Call - Model:', finalModel, 'Provider:', providerName);
    console.log('💬 Message history:', finalMessages.map(m => m.content).filter(Boolean));

    const emitToken = async (update) => {
        if (typeof onToken !== 'function') {
            return;
        }
        try {
            await onToken(update);
        } catch (callbackError) {
            console.error('❌ Error in stream callback:', callbackError);
        }
    };

    if (usingGemini) {
        const responseText = await generateWithGemini(apiKey, baseURL, finalModel, finalMessages);
        await emitToken({ delta: responseText, full: responseText, done: true, success: true });
        return responseText;
    }

    const endpoint = buildChatEndpoint(baseURL);
    const payload = {
        model: finalModel,
        messages: finalMessages,
        temperature,
        stream: true
    };

    const decoder = new TextDecoder('utf-8');
    let fullResponse = '';
    let streamSucceeded = false;

    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: buildAuthHeaders(apiKey, baseURL, 'text/event-stream'),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`Streaming request failed with status ${response.status}`);
        }

        if (!response.body) {
            throw new Error('Streaming response did not include a body');
        }

        const reader = response.body.getReader();
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            let newlineIndex;
            while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
                const rawLine = buffer.slice(0, newlineIndex);
                buffer = buffer.slice(newlineIndex + 1);

                const line = rawLine.trim();
                if (!line) continue;
                if (line.startsWith(':')) continue; // SSE comment / heartbeat

                if (!line.startsWith('data:')) {
                    continue;
                }

                const data = line.slice(5).trim();

                if (data === '[DONE]') {
                    streamSucceeded = true;
                    break;
                }

                try {
                    const parsed = JSON.parse(data);
                    const choice = parsed?.choices?.[0];
                    const delta = choice?.delta?.content || '';

                    if (delta) {
                        fullResponse += delta;
                        await emitToken({ delta, full: fullResponse, done: false, success: true });
                    }

                    const finishReason = choice?.finish_reason;
                    if (finishReason && finishReason !== 'stop' && finishReason !== null) {
                        console.warn('⚠️ Non-stop finish reason:', finishReason);
                    }
                } catch (parseError) {
                    console.warn('⚠️ Failed to parse SSE chunk:', parseError, data);
                }
            }
        }

        await reader.cancel().catch(() => {});

        if (streamSucceeded) {
            fullResponse = (fullResponse || '').trim();
            if (!fullResponse) {
                throw new Error('Stream completed without data');
            }
            await emitToken({ delta: '', full: fullResponse, done: true, success: true });
            console.log('✅ AI response:', fullResponse);
            return fullResponse;
        }

        console.warn('⚠️ Stream ended without [DONE] marker, falling back to standard completion');
    } catch (streamError) {
        console.warn('⚠️ Streaming failed, falling back to standard completion:', streamError);
    }

    // Fallback to non-streaming request
    const fallbackPayload = { ...payload, stream: false };
    try {
        const fallbackResponse = await fetch(endpoint, {
            method: 'POST',
            headers: buildAuthHeaders(apiKey, baseURL),
            body: JSON.stringify(fallbackPayload)
        });

        if (!fallbackResponse.ok) {
            throw new Error(`Request failed with status ${fallbackResponse.status}`);
        }

        const data = await fallbackResponse.json();
        const responseText = data?.choices?.[0]?.message?.content || '';
        const trimmed = responseText.trim();

        if (!trimmed) {
            throw new Error('AI generated empty response');
        }

        await emitToken({ delta: trimmed, full: trimmed, done: true, success: true });
        console.log('✅ AI response (fallback):', trimmed);
        return trimmed;
    } catch (fallbackError) {
        await emitToken({
            delta: '',
            full: '',
            done: true,
            success: false,
            error: true,
            errorMessage: fallbackError.message
        });
        throw fallbackError;
    }
}

export async function generateAIIdeasMultipleModels(
    providerConfigs,
    selectedNodeText,
    connectedNodes = [],
    models = [],
    onModelComplete = null,
    systemPrompt = null,
    onModelUpdate = null
) {
    if (!models || models.length === 0) {
        throw new Error('No models specified');
    }

    const descriptors = normalizeModelDescriptors(models);

    if (descriptors.length === 0) {
        throw new Error('No valid models specified');
    }

    console.log(
        `🚀 Starting parallel generation with ${descriptors.length} models:`,
        descriptors.map(descriptor => descriptor.name)
    );

    const resolveSystemPromptForModel = (descriptor) => {
        if (systemPrompt && typeof systemPrompt === 'object' && !Array.isArray(systemPrompt)) {
            const candidates = [];
            if (descriptor && typeof descriptor === 'object') {
                if (descriptor.key) {
                    candidates.push(descriptor.key);
                }
                if (descriptor.name) {
                    candidates.push(descriptor.name);
                }
            } else if (typeof descriptor === 'string') {
                candidates.push(descriptor);
            }

            for (const candidate of candidates) {
                if (!candidate) {
                    continue;
                }
                const promptValue = systemPrompt[candidate];
                if (typeof promptValue === 'string') {
                    const normalized = promptValue.trim();
                    if (normalized.length > 0) {
                        return normalized;
                    }
                }
            }
        }

        if (typeof systemPrompt === 'string' && systemPrompt.trim().length > 0) {
            return systemPrompt.trim();
        }

        return '';
    };

    // Create promises for all models to run in parallel
    const modelPromises = descriptors.map(async (descriptor) => {
        const { name, key, label } = descriptor;
        let providerKey = null;
        try {
            const credentials = resolveModelCredentials(name, providerConfigs);
            providerKey = credentials.providerKey;
            console.log(`🤖 Starting generation with model: ${name} (provider: ${providerKey || 'unknown'})`);
            const messages = buildIdeaMessages(selectedNodeText, connectedNodes);
            const promptForModel = resolveSystemPromptForModel(descriptor);

            const ideasText = await streamChatCompletion({
                apiKey: credentials.apiKey,
                baseURL: credentials.baseURL,
                messages,
                model: name,
                systemPrompt: promptForModel,
                onToken: async (update) => {
                    if (typeof onModelUpdate === 'function') {
                        await onModelUpdate({
                            model: name,
                            modelKey: key,
                            label,
                            ...update,
                            provider: providerKey
                        });
                    }
                }
            });

            const trimmed = ideasText.trim();
            const ideas = trimmed ? [trimmed] : [];
            const success = ideas.length > 0;

            const result = {
                model: name,
                modelKey: key,
                label,
                ideas: ideas,
                success,
                provider: providerKey,
                timestamp: Date.now()
            };

            if (!success) {
                result.error = true;
                result.errorMessage = 'Model returned no ideas';
            }

            if (onModelComplete && typeof onModelComplete === 'function') {
                try {
                    await onModelComplete(result);
                } catch (callbackError) {
                    console.error(`❌ Error in onModelComplete callback for ${name}:`, callbackError);
                }
            }

            console.log(`✅ Completed generation with model: ${name}`);
            return result;
        } catch (error) {
            console.error(`❌ Error with model ${name}:`, error.message);

            const result = {
                model: name,
                modelKey: key,
                label,
                ideas: [`Error with ${label}: ${error.message}`],
                success: false,
                error: true,
                errorMessage: error.message,
                provider: providerKey,
                timestamp: Date.now()
            };

            if (typeof onModelUpdate === 'function') {
                try {
                    await onModelUpdate({
                        model: name,
                        modelKey: key,
                        label,
                        success: false,
                        done: true,
                        error: true,
                        errorMessage: error.message,
                        provider: providerKey
                    });
                } catch (callbackError) {
                    console.error(`❌ Error in onModelUpdate callback for ${model}:`, callbackError);
                }
            }

            // Call the callback even for errors
            if (onModelComplete && typeof onModelComplete === 'function') {
                try {
                    await onModelComplete(result);
                } catch (callbackError) {
                    console.error(`❌ Error in onModelComplete callback for ${name}:`, callbackError);
                }
            }

            return result;
        }
    });

    // Wait for all models to complete (or fail)
    const results = await Promise.allSettled(modelPromises);

    // Extract the actual results from Promise.allSettled
    const finalResults = results.map((result, index) => {
        if (result.status === 'fulfilled') {
            return result.value;
        } else {
            // This should rarely happen since we handle errors inside the promise
            console.error('❌ Unexpected promise rejection:', result.reason);
            const fallbackDescriptor = descriptors[index] || { name: 'unknown', key: `unknown__${index}`, label: 'unknown' };
            return {
                model: fallbackDescriptor.name,
                modelKey: fallbackDescriptor.key,
                label: fallbackDescriptor.label,
                ideas: [`Unexpected error: ${result.reason?.message || 'Unknown error'}`],
                success: false,
                error: true,
                errorMessage: result.reason?.message || 'Unknown error',
                timestamp: Date.now()
            };
        }
    });

    const successCount = finalResults.filter(r => r.success).length;
    const errorCount = finalResults.filter(r => !r.success).length;

    console.log(`🏁 Parallel generation completed: ${successCount} successful, ${errorCount} failed`);

    return finalResults;
}

// ============================================================================
// AUTOMANAGED AI FUNCTIONS (Backend-Managed, OpenAI-Compatible)
// ============================================================================

// Automanaged mode uses backend API with request tracking
console.log('⚡ Automanaged AI service initialized - using backend proxy');

/**
 * Fetch available models from automanaged backend
 */
export async function fetchAutomanagedModels() {
    try {
        const response = await fetch('/api/automanaged/models');
        if (!response.ok) {
            throw new Error(`Failed to fetch models: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('❌ Error fetching automanaged models:', error);
        // Return default fallback
        return {
            models: [
                {
                    id: 'google/gemini-2.0-flash-exp:free',
                    name: 'Gemini 2.0 Flash (Free)',
                    description: 'Fast and capable, completely free tier from Google',
                    provider: 'Google',
                    providerKey: 'openrouter'
                },
                {
                    id: 'openai/gpt-oss-120b',
                    name: 'GPT-OSS 120B',
                    description: '120B open-source mixture with OpenAI-compatible responses via Groq',
                    provider: 'Groq',
                    providerKey: 'groq'
                },
                {
                    id: 'llama-3.3-70b-versatile',
                    name: 'Llama 3.3 70B Versatile',
                    description: 'Meta\'s flagship reasoning model hosted on Groq',
                    provider: 'Groq',
                    providerKey: 'groq'
                },
                {
                    id: 'qwen/qwen3-32b',
                    name: 'Qwen 3 32B',
                    description: 'Aligned Qwen 3 32B instruct model served by Groq',
                    provider: 'Groq',
                    providerKey: 'groq'
                }
            ],
            defaultModel: 'google/gemini-2.0-flash-exp:free'
        };
    }
}

/**
 * Generate AI ideas using automanaged backend (OpenAI-compatible)
 * Supports request tracking and limits
 */
export async function generateAIIdeasAutomanaged(
    selectedNodeText,
    connectedNodes = [],
    userId = null,
    sessionId = null,
    systemPrompt = null,
    onToken = null,
    model = null
) {
    console.log('⚡ Using Automanaged Mode');

    const messages = buildIdeaMessages(selectedNodeText, connectedNodes);
    console.log('💬 Message history:', messages.map(m => m.content));

    try {
        const response = await fetch('/api/automanaged/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId,
                sessionId,
                messages,
                systemPrompt,
                model
            })
        });

        if (response.status === 429) {
            const errorData = await response.json();
            throw new Error(errorData.isGuest 
                ? 'You\'ve reached your 15 free requests. Please login for 20 more!' 
                : 'You\'ve reached your 20 free requests. Add your own API keys for unlimited access.');
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `API error: ${response.status}`);
        }

        // Handle SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n').filter(line => line.trim() !== '');

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const data = line.slice(6);
                    
                    if (data === '[DONE]') {
                        continue;
                    }

                    try {
                        const parsed = JSON.parse(data);
                        
                        if (parsed.error) {
                            throw new Error(parsed.error);
                        }

                        if (parsed.content) {
                            fullContent = parsed.full || fullContent + parsed.content;
                            
                            if (typeof onToken === 'function') {
                                await onToken({
                                    content: parsed.content,
                                    full: fullContent,
                                    done: parsed.done || false,
                                    success: true
                                });
                            }
                        }
                    } catch (e) {
                        console.warn('Failed to parse SSE data:', e);
                    }
                }
            }
        }

        const trimmedResponse = fullContent.trim();
        if (!trimmedResponse) {
            throw new Error('Automanaged API generated empty response');
        }

        console.log('✅ Automanaged response:', trimmedResponse);
        return [trimmedResponse];

    } catch (apiError) {
        console.error('❌ Automanaged API Error:', apiError.message);
        
        if (typeof onToken === 'function') {
            try {
                await onToken({
                    success: false,
                    done: true,
                    error: true,
                    errorMessage: apiError.message
                });
            } catch (callbackError) {
                console.error('❌ Error notifying automanaged stream failure:', callbackError);
            }
        }
        
        throw apiError;
    }
}


/**
 * Generate AI ideas using automanaged backend for single model (backend uses configured model)
 * Note: In automanaged mode, all requests use the same backend model
 * The 'models' parameter is kept for API compatibility but all use the same backend
 */
export async function generateAIIdeasMultipleAutomanaged(
    selectedNodeText,
    connectedNodes = [],
    models = [],
    userId = null,
    sessionId = null,
    onModelComplete = null,
    systemPrompt = null,
    onModelUpdate = null
) {
    if (!models || models.length === 0) {
        throw new Error('No models specified');
    }

    const descriptors = normalizeModelDescriptors(models);

    if (descriptors.length === 0) {
        throw new Error('No valid models specified');
    }

    console.log(`⚡ Starting automanaged generation with ${descriptors.length} model request(s)`);

    const resolveSystemPromptForModel = (descriptor) => {
        if (systemPrompt && typeof systemPrompt === 'object' && !Array.isArray(systemPrompt)) {
            const candidates = [];
            if (descriptor && typeof descriptor === 'object') {
                if (descriptor.key) {
                    candidates.push(descriptor.key);
                }
                if (descriptor.name) {
                    candidates.push(descriptor.name);
                }
            } else if (typeof descriptor === 'string') {
                candidates.push(descriptor);
            }

            for (const candidate of candidates) {
                if (!candidate) {
                    continue;
                }
                const promptValue = systemPrompt[candidate];
                if (typeof promptValue === 'string') {
                    const normalized = promptValue.trim();
                    if (normalized.length > 0) {
                        return normalized;
                    }
                }
            }
        }

        if (typeof systemPrompt === 'string' && systemPrompt.trim().length > 0) {
            return systemPrompt.trim();
        }

        return '';
    };

    // Create promises for all model requests (all use same backend model)
    const modelPromises = descriptors.map(async (descriptor) => {
        const { name, key, label } = descriptor;
        try {
            console.log(`🤖 Starting automanaged generation for: ${label}`);
            const promptForModel = resolveSystemPromptForModel(descriptor);
            
            const ideas = await generateAIIdeasAutomanaged(
                selectedNodeText, 
                connectedNodes, 
                userId,
                sessionId,
                promptForModel, 
                async (update) => {
                    if (typeof onModelUpdate === 'function') {
                        await onModelUpdate({
                            model: name,
                            modelKey: key,
                            label,
                            ...update,
                            provider: 'automanaged'
                        });
                    }
                },
                name // Pass the model ID from descriptor
            );

            const success = Array.isArray(ideas) && ideas.length > 0;
            const result = {
                model: name,
                modelKey: key,
                label,
                ideas: success ? ideas : [`Automanaged error with ${label}: Model returned no ideas`],
                success,
                provider: 'automanaged',
                timestamp: Date.now()
            };

            if (!success) {
                result.error = true;
                result.errorMessage = 'Model returned no ideas';
            }

            if (onModelComplete && typeof onModelComplete === 'function') {
                try {
                    await onModelComplete(result);
                } catch (callbackError) {
                    console.error(`❌ Error in onModelComplete callback for ${name}:`, callbackError);
                }
            }

            console.log(`✅ Completed automanaged generation for: ${label}`);
            return result;
        } catch (error) {
            console.error(`❌ Error with automanaged model ${name}:`, error.message);

            const result = {
                model: name,
                modelKey: key,
                label,
                ideas: [`Automanaged error with ${label}: ${error.message}`],
                success: false,
                error: true,
                errorMessage: error.message,
                provider: 'automanaged',
                timestamp: Date.now()
            };

            if (typeof onModelUpdate === 'function') {
                try {
                    await onModelUpdate({
                        model: name,
                        modelKey: key,
                        label,
                        success: false,
                        done: true,
                        error: true,
                        errorMessage: error.message,
                        provider: 'automanaged'
                    });
                } catch (callbackError) {
                    console.error(`❌ Error in onModelUpdate callback for ${name}:`, callbackError);
                }
            }

            if (onModelComplete && typeof onModelComplete === 'function') {
                try {
                    await onModelComplete(result);
                } catch (callbackError) {
                    console.error(`❌ Error in onModelComplete callback for ${name}:`, callbackError);
                }
            }

            return result;
        }
    });

    const results = await Promise.allSettled(modelPromises);

    const finalResults = results.map((result, index) => {
        if (result.status === 'fulfilled') {
            return result.value;
        } else {
            console.error('❌ Unexpected promise rejection:', result.reason);
            const fallbackDescriptor = descriptors[index] || { name: 'unknown', key: `unknown__${index}`, label: 'unknown' };
            return {
                model: fallbackDescriptor.name,
                modelKey: fallbackDescriptor.key,
                label: fallbackDescriptor.label,
                ideas: [`Unexpected automanaged error: ${result.reason?.message || 'Unknown error'}`],
                success: false,
                error: true,
                errorMessage: result.reason?.message || 'Unknown error',
                provider: 'automanaged',
                timestamp: Date.now()
            };
        }
    });

    const successCount = finalResults.filter(r => r.success).length;
    const errorCount = finalResults.filter(r => !r.success).length;

    console.log(`🏁 Automanaged generation completed: ${successCount} successful, ${errorCount} failed`);

    return finalResults;
}

/**
 * Generate an image using Replicate API (text-to-image)
 * @param {string} prompt - The prompt for image generation
 * @param {string} replicateApiKey - The user's Replicate API key
 * @param {object} options - Optional parameters (model, aspect_ratio, etc.)
 * @returns {Promise<string>} - Resolves to the image URL
 */
export async function generateReplicateImage(prompt, replicateApiKey, options = {}) {
    if (!replicateApiKey || !replicateApiKey.startsWith('r8_')) {
        throw new Error('Valid Replicate API key required.');
    }

    const { model = 'bytedance/seedream-3', aspectRatio = '1:1' } = options;

    // Use the proxy server for Replicate API calls to protect the API key
    const submitRes = await fetch('/api/replicate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            prompt, 
            replicateApiKey,
            model, // Pass model to proxy
            aspectRatio // Pass aspect ratio to proxy
        })
    });

    if (!submitRes.ok) {
        const err = await submitRes.text();
        throw new Error('Replicate proxy error: ' + err);
    }
    const submitData = await submitRes.json();
    const getUrl = submitData.urls.get;
    // 2. Poll for result (now via proxy)
    let status = submitData.status;
    let outputUrl = null;
    let pollCount = 0;
    while (status !== 'succeeded' && status !== 'failed' && pollCount < 40) {
        await new Promise(r => setTimeout(r, 2500));
        const pollRes = await fetch('/api/replicate-poll', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ getUrl, replicateApiKey })
        });
        if (!pollRes.ok) {
            throw new Error('Polling error: ' + (await pollRes.text()));
        }
        const pollData = await pollRes.json();
        status = pollData.status;
        if (status === 'succeeded') {
            outputUrl = Array.isArray(pollData.output) ? pollData.output[0] : pollData.output;
        } else if (status === 'failed') {
            throw new Error('Image generation failed: ' + (pollData.error || 'Unknown error'));
        }
        pollCount++;
    }
    if (!outputUrl) throw new Error('Image generation timed out or failed.');
    return outputUrl;
}

export async function generateImagesMultipleModels(prompt, replicateApiKey, models = [], onModelComplete = null, options = {}) {
    if (!models || models.length === 0) {
        throw new Error('No image models specified');
    }

    const { aspectRatio = '1:1' } = options;

    console.log(`🚀 Starting parallel image generation with ${models.length} models:`, models);

    const modelPromises = models.map(async (model) => {
        try {
            console.log(`🖼️ Starting image generation with model: ${model}`);
            const imageUrl = await generateReplicateImage(prompt, replicateApiKey, { 
                model, 
                aspectRatio 
            });

            const result = {
                model: model,
                imageUrl: imageUrl,
                success: true,
                timestamp: Date.now()
            };

            if (onModelComplete) {
                try {
                    await onModelComplete(result);
                } catch (callbackError) {
                    console.error(`❌ Error in onModelComplete callback for ${model}:`, callbackError);
                }
            }

            console.log(`✅ Completed image generation with model: ${model}`);
            return result;
        } catch (error) {
            console.error(`❌ Error with image model ${model}:`, error.message);

            const result = {
                model: model,
                imageUrl: null,
                success: false,
                errorMessage: error.message,
                timestamp: Date.now()
            };

            if (onModelComplete) {
                try {
                    await onModelComplete(result);
                } catch (callbackError) {
                    console.error(`❌ Error in onModelComplete callback for ${model}:`, callbackError);
                }
            }
            return result;
        }
    });

    const results = await Promise.allSettled(modelPromises);
    const finalResults = results.map(r => r.status === 'fulfilled' ? r.value : r.reason);

    const successCount = finalResults.filter(r => r.success).length;
    const errorCount = finalResults.filter(r => !r.success).length;
    console.log(`🏁 Parallel image generation completed: ${successCount} successful, ${errorCount} failed`);

    return finalResults;
}
